import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  FaSort,
  FaSearch,
  FaEdit,
  FaTrash,
  FaTimes,
  FaPlus,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  signOut,
} from "firebase/auth";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { callDeleteUserAuthHttp, db, secondaryAuth } from "../../config/firebase";
import { usePersonnel } from "../../hooks/usePersonnel";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useAuth } from "../../hooks/useAuth";
import "./Users.css";

const permissionsLabel = (permissions) => {
  switch (permissions) {
    case "admin":
      return "מנהל";
    case "parentsUser":
      return "מחלקת קשר";
    case "childUser":
      return 'משק"ש';
    default:
      return permissions || "לא זמין";
  }
};

const UsersMobileCard = React.memo(function UsersMobileCard({
  user,
  currentUserId,
  getUserDisplayName,
  handleEdit,
  handleDeleteUser,
}) {
  const isCurrent = user.email === currentUserId;
  return (
    <div
      className={`users-mobile-card${isCurrent ? " users-mobile-card-current" : ""}`}
    >
      <div className="users-mobile-card-header">
        <span className="users-mobile-display-name">
          {getUserDisplayName(user)}
        </span>
        {isCurrent && (
          <span className="current-user-badge">נוכחי</span>
        )}
      </div>
      <div className="users-mobile-card-body">
        <div className="users-mobile-field">
          <span className="users-mobile-label">אימייל</span>
          <span className="users-mobile-value">{user.email || "לא זמין"}</span>
        </div>
        <div className="users-mobile-field">
          <span className="users-mobile-label">הרשאות</span>
          <span className="users-mobile-value">
            {permissionsLabel(user.permissions)}
          </span>
        </div>
        <div className="users-mobile-field">
          <span className="users-mobile-label">נוצר ב</span>
          <span className="users-mobile-value">{user.createdAt || "לא זמין"}</span>
        </div>
        <div className="users-mobile-field">
          <span className="users-mobile-label">עודכן ב</span>
          <span className="users-mobile-value">{user.updatedAt || "לא זמין"}</span>
        </div>
      </div>
      <div className="users-mobile-card-actions">
        <button
          type="button"
          className="users-mobile-action-btn edit-btn"
          onClick={() => handleEdit(user)}
          title="ערוך"
        >
          <FaEdit /> ערוך
        </button>
        {user.email !== currentUserId && (
          <button
            type="button"
            className="users-mobile-action-btn delete-btn"
            onClick={() => handleDeleteUser(user.email, user.id)}
            title="מחק"
          >
            <FaTrash /> מחק
          </button>
        )}
      </div>
    </div>
  );
});

// Memoized UserRow component for better performance
const UserRow = React.memo(
  ({
    user,
    currentUserId,
    getUserDisplayName,
    handleEdit,
    handleDeleteUser,
  }) => (
    <tr
      key={user.email}
      className={user.email === currentUserId ? "current-user-row" : ""}
    >
      <td>
        {getUserDisplayName(user)}
        {user.email === currentUserId && (
          <span className="current-user-badge">(נוכחי)</span>
        )}
      </td>
      <td>{user.email || "לא זמין"}</td>
      <td>{user.permissions || "לא זמין"}</td>
      <td>{user.createdAt || "לא זמין"}</td>
      <td>{user.updatedAt || "לא זמין"}</td>
      <td className="actions">
        <button
          className="edit-btn"
          onClick={() => handleEdit(user)}
          title="ערוך"
        >
          <FaEdit />
        </button>
        {user.email !== currentUserId && (
          <button
            className="delete-btn"
            onClick={() => handleDeleteUser(user.email, user.id)}
            title="מחק"
          >
            <FaTrash />
          </button>
        )}
      </td>
    </tr>
  )
);

const STAT_FILTER_ALL = "all";
const STAT_FILTER_ADMIN = "admin";
const STAT_FILTER_PARENTS = "parentsUser";
const STAT_FILTER_CHILD = "childUser";

const Users = () => {
  const isMobile = useIsMobile();
  const { personnel } = usePersonnel();
  const { user: authUser } = useAuth();
  const currentUserId = authUser?.email || authUser?.id || "";
  const currentUserPermissions = authUser?.permissions || "childUser";
  const isAdmin = currentUserPermissions === "admin";
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statFilter, setStatFilter] = useState(STAT_FILTER_ALL);
  const [editingUser, setEditingUser] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [personnelSearchTerm, setPersonnelSearchTerm] = useState("");
  const [showPersonnelOptions, setShowPersonnelOptions] = useState(false);
  const [addPersonnelSearchTerm, setAddPersonnelSearchTerm] = useState("");
  const [showAddPersonnelOptions, setShowAddPersonnelOptions] = useState(false);
  const [editForm, setEditForm] = useState({
    employeeId: "",
    email: "",
    permissions: "",
    password: "",
  });
  const [addForm, setAddForm] = useState({
    employeeId: "",
    email: "",
    permissions: "parentsUser",
    password: "",
  });

  // Helper function to get personnel data by employeeId
  const getPersonnelByEmployeeId = useCallback(
    (employeeId) => {
      return personnel.find((p) => p.personalNumber === employeeId);
    },
    [personnel]
  );

  // Helper function to get display name for user
  const getUserDisplayName = useCallback(
    (user) => {
      const personnelData = getPersonnelByEmployeeId(user.employeeId);
      if (personnelData) {
        return `${personnelData.firstName} ${personnelData.lastName} (מ.א ${personnelData.personalNumber})`;
      }
      return user.email; // Fallback to email if no personnel data
    },
    [getPersonnelByEmployeeId]
  );

  // Optimized personnel search function
  const searchPersonnel = useCallback((searchTerm, personnelList) => {
    if (!searchTerm.trim()) return [];

    const term = searchTerm.toLowerCase();
    return personnelList
      .filter(
        (p) =>
          p.firstName?.toLowerCase().includes(term) ||
          p.lastName?.toLowerCase().includes(term) ||
          p.personalNumber?.includes(term) ||
          `${p.firstName} ${p.lastName}`.toLowerCase().includes(term)
      )
      .slice(0, 10); // Limit to 10 results
  }, []);

  // Filtered personnel for search
  const filteredPersonnel = useMemo(() => {
    return searchPersonnel(personnelSearchTerm, personnel);
  }, [personnel, personnelSearchTerm, searchPersonnel]);

  // Filtered personnel for add form search
  const filteredAddPersonnel = useMemo(() => {
    return searchPersonnel(addPersonnelSearchTerm, personnel);
  }, [personnel, addPersonnelSearchTerm, searchPersonnel]);

  // Handle personnel selection - memoized for performance
  const handlePersonnelSelect = useCallback((personnelData) => {
    setEditForm((prev) => ({
      ...prev,
      employeeId: personnelData.personalNumber,
    }));
    setPersonnelSearchTerm(
      `${personnelData.firstName} ${personnelData.lastName} (מ.א ${personnelData.personalNumber})`
    );
    setShowPersonnelOptions(false);
  }, []);

  // Handle add form personnel selection - memoized for performance
  const handleAddPersonnelSelect = useCallback((personnelData) => {
    setAddForm((prev) => ({
      ...prev,
      employeeId: personnelData.personalNumber,
    }));
    setAddPersonnelSearchTerm(
      `${personnelData.firstName} ${personnelData.lastName} (מ.א ${personnelData.personalNumber})`
    );
    setShowAddPersonnelOptions(false);
  }, []);
  const [emailErrors, setEmailErrors] = useState({
    edit: "",
    add: "",
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cache, setCache] = useState({
    lastFetch: null,
    data: [],
    isStale: true,
  });

  // Debounced search for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cache management
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const isCacheValid = useCallback(() => {
    if (!cache.lastFetch) return false;
    return Date.now() - cache.lastFetch < CACHE_DURATION && !cache.isStale;
  }, [cache.lastFetch, cache.isStale]);

  // Helper function to convert Firestore timestamps to readable dates
  const formatFirestoreDate = (timestamp) => {
    if (!timestamp) return "לא זמין";

    // If it's already a formatted string, return it
    if (typeof timestamp === "string" && timestamp !== "לא זמין") {
      return timestamp;
    }

    // If it's a Firestore Timestamp object
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("he-IL");
    }

    // If it's already a Date object
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString("he-IL");
    }

    // If it's a string, try to parse it
    try {
      return new Date(timestamp).toLocaleString("he-IL");
    } catch (error) {
      return "לא זמין";
    }
  };

  // Real-time database listener with caching
  useEffect(() => {
    if (!isAdmin) return;

    const usersRef = collection(db, "users");
    const q = query(usersRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const usersData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id, // This is now the email
            email: doc.id, // Email is the document ID
            ...data,
            // Convert Firestore timestamps to readable strings
            createdAt: formatFirestoreDate(data.createdAt),
            updatedAt: formatFirestoreDate(data.updatedAt),
          };
        });

        // Update cache
        setCache({
          lastFetch: Date.now(),
          data: usersData,
          isStale: false,
        });

        setUsers(usersData);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching users:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isAdmin]);

  // Redirect non-admin users
  useEffect(() => {
    if (!isAdmin) {
      window.location.href = "/ראשי";
    }
  }, [isAdmin]);

  // Optimized user search function with personnel data
  const searchUsers = useCallback(
    (usersList, searchTerm) => {
      if (!searchTerm.trim()) return usersList;

      const term = searchTerm.toLowerCase();
      return usersList.filter((user) => {
        // Use early returns for better performance
        if (user.employeeId?.toLowerCase().includes(term)) return true;
        if (user.email?.toLowerCase().includes(term)) return true;
        if (user.permissions?.toLowerCase().includes(term)) return true;
        if (user.createdAt?.toLowerCase().includes(term)) return true;
        if (user.updatedAt?.toLowerCase().includes(term)) return true;

        // Search in personnel data (employee name and ID)
        const personnelData = getPersonnelByEmployeeId(user.employeeId);
        if (personnelData) {
          if (personnelData.firstName?.toLowerCase().includes(term))
            return true;
          if (personnelData.lastName?.toLowerCase().includes(term)) return true;
          if (personnelData.personalNumber?.includes(term)) return true;
          if (
            `${personnelData.firstName} ${personnelData.lastName}`
              .toLowerCase()
              .includes(term)
          )
            return true;
        }

        return false;
      });
    },
    [getPersonnelByEmployeeId]
  );

  const usersMatchingStat = useMemo(() => {
    if (statFilter === STAT_FILTER_ALL) return users;
    return users.filter((u) => u.permissions === statFilter);
  }, [users, statFilter]);

  // Filter by search within the permission group selected from statistics
  const filteredUsers = useMemo(() => {
    return searchUsers(usersMatchingStat, debouncedSearchTerm);
  }, [usersMatchingStat, debouncedSearchTerm, searchUsers]);

  const emptyListMessage = useMemo(() => {
    if (!users.length) return "אין משתמשים במערכת";
    if (!usersMatchingStat.length) {
      if (statFilter === STAT_FILTER_ADMIN) return "אין מנהלים במערכת";
      if (statFilter === STAT_FILTER_PARENTS)
        return "אין משתמשי מחלקת קשר במערכת";
      if (statFilter === STAT_FILTER_CHILD)
        return 'אין משתמשי משק"ש במערכת';
      return "אין משתמשים במערכת";
    }
    if (!filteredUsers.length) {
      return "לא נמצאו משתמשים התואמים לחיפוש";
    }
    return "";
  }, [
    users.length,
    usersMatchingStat.length,
    filteredUsers.length,
    statFilter,
  ]);

  // Optimized statistics calculation
  const statistics = useMemo(() => {
    if (!users.length) {
      return { total: 0, admin: 0, parentsUser: 0, childUser: 0 };
    }

    // Single pass through users array for better performance
    const stats = users.reduce(
      (acc, user) => {
        acc.total++;
        switch (user.permissions) {
          case "admin":
            acc.admin++;
            break;
          case "parentsUser":
            acc.parentsUser++;
            break;
          case "childUser":
            acc.childUser++;
            break;
        }
        return acc;
      },
      { total: 0, admin: 0, parentsUser: 0, childUser: 0 }
    );
    return stats;
  }, [users]);

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      employeeId: user.employeeId,
      email: user.email,
      permissions: user.permissions,
      password: "",
    });

    // Set personnel search term if user has personnel data
    const personnelData = getPersonnelByEmployeeId(user.employeeId);
    if (personnelData) {
      setPersonnelSearchTerm(
        `${personnelData.firstName} ${personnelData.lastName} (מ.א ${personnelData.personalNumber})`
      );
    } else {
      setPersonnelSearchTerm("");
    }
  };

  // Check if current user is editing themselves (using email as ID)
  const isEditingSelf = editingUser && editingUser.email === currentUserId;

  // Password visibility toggle
  const togglePasswordVisibility = () => {
    setShowEditPassword(!showEditPassword);
  };

  // Optimized email validation functions - memoized for performance
  const isEmailUnique = useCallback(
    (email, excludeUserId = null) => {
      if (!email) return false;

      const emailLower = email.toLowerCase();
      return !users.some(
        (user) =>
          user.email.toLowerCase() === emailLower &&
          user.email !== excludeUserId
      );
    },
    [users]
  );

  const validateEmailUniqueness = useCallback(
    (email, excludeUserId = null) => {
      if (!email) return { isValid: false, message: "אימייל נדרש" };

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return { isValid: false, message: "פורמט אימייל לא תקין" };
      }

      if (!isEmailUnique(email, excludeUserId)) {
        return { isValid: false, message: "אימייל זה כבר קיים במערכת" };
      }

      return { isValid: true, message: "" };
    },
    [isEmailUnique]
  );

  // Optimized form input handlers - memoized for performance
  const handleEditInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setEditForm((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Validate email in real-time
      if (name === "email") {
        const validation = validateEmailUniqueness(value, editingUser?.email);
        setEmailErrors((prev) => ({
          ...prev,
          edit: validation.isValid ? "" : validation.message,
        }));
      }
    },
    [validateEmailUniqueness, editingUser?.email]
  );

  const handleAddInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setAddForm((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Validate email in real-time
      if (name === "email") {
        const validation = validateEmailUniqueness(value);
        setEmailErrors((prev) => ({
          ...prev,
          add: validation.isValid ? "" : validation.message,
        }));
      }
    },
    [validateEmailUniqueness]
  );

  const handleAddUser = async (e) => {
    e.preventDefault();

    const emailNormalized = addForm.email.trim().toLowerCase();

    if (!addForm.password?.trim()) {
      alert("נא להזין סיסמה — היא נדרשת ליצירת חשבון התחברות ב-Firebase.");
      return;
    }

    const emailValidation = validateEmailUniqueness(addForm.email);
    if (!emailValidation.isValid) {
      setEmailErrors((prev) => ({
        ...prev,
        add: emailValidation.message,
      }));
      return;
    }

    let createdCredential = null;
    try {
      createdCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        emailNormalized,
        addForm.password
      );
      const uid = createdCredential.user.uid;

      const userData = {
        id: uid,
        employeeId: addForm.employeeId,
        email: emailNormalized,
        permissions: addForm.permissions,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const userRef = doc(db, "users", emailNormalized);
      await setDoc(userRef, userData);

      await signOut(secondaryAuth);

      setAddForm({
        employeeId: "",
        email: "",
        permissions: "parentsUser",
        password: "",
      });
      setAddPersonnelSearchTerm("");
      setShowAddModal(false);
      setEmailErrors((prev) => ({ ...prev, add: "" }));
    } catch (error) {
      console.error("Error adding user:", error);
      if (createdCredential?.user) {
        try {
          await deleteUser(createdCredential.user);
        } catch (cleanupErr) {
          console.error("Rollback Auth user failed:", cleanupErr);
        }
        await signOut(secondaryAuth);
      }
      const code = error?.code;
      if (code === "auth/email-already-in-use") {
        alert(
          "האימייל כבר רשום ב-Firebase Authentication. מחקו אותו מהקונסול או השתמשו באימייל אחר."
        );
      } else if (code === "auth/weak-password") {
        alert("הסיסמה חלשה מדי. נדרשת סיסמה חזקה יותר.");
      } else if (code === "auth/invalid-email") {
        alert("כתובת האימייל לא תקינה.");
      } else {
        alert("שגיאה בהוספת משתמש. נסו שוב.");
      }
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    // Validate email before submission
    const emailValidation = validateEmailUniqueness(
      editForm.email,
      editingUser.email
    );
    if (!emailValidation.isValid) {
      setEmailErrors((prev) => ({
        ...prev,
        edit: emailValidation.message,
      }));
      return;
    }

    // Additional client-side validation to prevent permission changes for self
    if (isEditingSelf) {
      // Ensure permissions field is not included in the update

      // Prevent email changes for self to avoid authentication issues
      if (editForm.email !== editingUser.email) {
        alert("לא ניתן לשנות את האימייל של המשתמש הנוכחי");
        return;
      }
    }

    try {
      // Always use the original email as document ID to avoid creating new users
      const userRef = doc(db, "users", editingUser.email);
      const updateData = {
        employeeId: editForm.employeeId,
        updatedAt: serverTimestamp(),
      };

      // Only update permissions if not editing self
      if (!isEditingSelf) {
        updateData.permissions = editForm.permissions;
      }

      // Only update email if it's different and not editing self
      if (editForm.email !== editingUser.email && !isEditingSelf) {
        updateData.email = editForm.email;
      }

      // Use setDoc with merge to handle cases where document might not exist
      await setDoc(
        userRef,
        {
          ...editingUser, // Keep existing data
          ...updateData, // Apply updates
        },
        { merge: true }
      );

      setEditingUser(null);
      setEditForm({
        employeeId: "",
        email: "",
        permissions: "",
        password: "",
      });
      setPersonnelSearchTerm("");
      setEmailErrors((prev) => ({ ...prev, edit: "" }));

      // The real-time listener will automatically update the UI
    } catch (error) {
      console.error("Error updating user:", error);
      alert("שגיאה בעדכון משתמש");
    }
  };

  const handleDeleteUser = useCallback(
    async (userEmail, userAuthUid) => {
      if (userEmail === currentUserId) {
        alert(
          "🚫 לא ניתן למחוק את המשתמש הנוכחי\n\nהסיבה: מניעת אובדן גישה למערכת"
        );
        return;
      }

      if (
        window.confirm(
          "האם אתה בטוח שברצונך למחוק משתמש זה?\n\nפעולה זו לא ניתנת לביטול!"
        )
      ) {
        try {
          await callDeleteUserAuthHttp({
            uid: userAuthUid !== userEmail ? userAuthUid : null,
            email: userEmail,
          });

          await deleteDoc(doc(db, "users", userEmail));
        } catch (error) {
          console.error("Error deleting user:", error);
          alert("שגיאה במחיקת משתמש");
        }
      }
    },
    [currentUserId]
  );

  // Don't render if not admin
  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div
        id="users-page-scope"
        className="user-management-container"
      >
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>טוען משתמשים...</p>
        </div>
      </div>
    );
  }

  return (
    <div id="users-page-scope" className="user-management-container">
      <div className="user-management-header">
        <h1>ניהול משתמשים</h1>
        <button className="add-user-btn" onClick={() => setShowAddModal(true)}>
          <FaPlus />
          הוסף משתמש
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="חיפוש משתמשים..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Statistics — click to filter the list below */}
      <div
        className="statistics-container"
        role="toolbar"
        aria-label="סינון לפי סוג משתמש"
      >
        <button
          type="button"
          className={`stat-card stat-card-filter${statFilter === STAT_FILTER_ALL ? " stat-card-filter-active" : ""}`}
          onClick={() => setStatFilter(STAT_FILTER_ALL)}
          aria-pressed={statFilter === STAT_FILTER_ALL}
        >
          <span className="stat-label">סה"כ משתמשים:</span>
          <span className="stat-value">{statistics.total}</span>
        </button>
        <button
          type="button"
          className={`stat-card stat-card-filter${statFilter === STAT_FILTER_ADMIN ? " stat-card-filter-active" : ""}`}
          onClick={() => setStatFilter(STAT_FILTER_ADMIN)}
          aria-pressed={statFilter === STAT_FILTER_ADMIN}
        >
          <span className="stat-label">מנהלים:</span>
          <span className="stat-value">{statistics.admin}</span>
        </button>
        <button
          type="button"
          className={`stat-card stat-card-filter${statFilter === STAT_FILTER_PARENTS ? " stat-card-filter-active" : ""}`}
          onClick={() => setStatFilter(STAT_FILTER_PARENTS)}
          aria-pressed={statFilter === STAT_FILTER_PARENTS}
        >
          <span className="stat-label">מחלקת קשר:</span>
          <span className="stat-value">{statistics.parentsUser}</span>
        </button>
        <button
          type="button"
          className={`stat-card stat-card-filter${statFilter === STAT_FILTER_CHILD ? " stat-card-filter-active" : ""}`}
          onClick={() => setStatFilter(STAT_FILTER_CHILD)}
          aria-pressed={statFilter === STAT_FILTER_CHILD}
        >
          <span className="stat-label">משק"ש:</span>
          <span className="stat-value">{statistics.childUser}</span>
        </button>
      </div>

      {isMobile ? (
        filteredUsers.length === 0 ? (
          <div className="users-mobile-empty">{emptyListMessage}</div>
        ) : (
          <div className="users-mobile-cards">
            {filteredUsers.map((user) => (
              <UsersMobileCard
                key={user.email}
                user={user}
                currentUserId={currentUserId}
                getUserDisplayName={getUserDisplayName}
                handleEdit={handleEdit}
                handleDeleteUser={handleDeleteUser}
              />
            ))}
            <p className="users-mobile-count" aria-live="polite">
              {`סה"כ ${filteredUsers.length} משתמשים`}
            </p>
          </div>
        )
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>
                  <FaSort /> שם מלא (מ.א)
                </th>
                <th>
                  <FaSort /> אימייל
                </th>
                <th>
                  <FaSort /> הרשאות
                </th>
                <th>
                  <FaSort /> נוצר ב
                </th>
                <th>
                  <FaSort /> עודכן ב
                </th>
                <th>פעילות</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {emptyListMessage}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <UserRow
                    key={user.email}
                    user={user}
                    currentUserId={currentUserId}
                    getUserDisplayName={getUserDisplayName}
                    handleEdit={handleEdit}
                    handleDeleteUser={handleDeleteUser}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="edit-modal">
            <div className="modal-header">
              <h2>עריכת משתמש</h2>
              <button
                className="close-btn"
                onClick={() => setEditingUser(null)}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="edit-form">
              <div className="form-group">
                <label htmlFor="edit-personnel-search">חיפוש איש צוות:</label>
                <div className="personnel-search-container">
                  <input
                    type="text"
                    id="edit-personnel-search"
                    placeholder="הקלד שם או מספר אישי..."
                    value={personnelSearchTerm}
                    onChange={(e) => {
                      setPersonnelSearchTerm(e.target.value);
                      setShowPersonnelOptions(true);
                    }}
                    onFocus={() => setShowPersonnelOptions(true)}
                    onBlur={() =>
                      setTimeout(() => setShowPersonnelOptions(false), 200)
                    }
                  />
                  {showPersonnelOptions && filteredPersonnel.length > 0 && (
                    <div className="personnel-options">
                      {filteredPersonnel.map((person) => (
                        <div
                          key={person.id}
                          className="personnel-option"
                          onClick={() => handlePersonnelSelect(person)}
                        >
                          {person.firstName} {person.lastName} (מ.א{" "}
                          {person.personalNumber})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="hidden"
                  name="employeeId"
                  value={editForm.employeeId}
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-email">אימייל:</label>
                <input
                  type="email"
                  id="edit-email"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditInputChange}
                  required
                  disabled={isEditingSelf}
                  className={emailErrors.edit ? "error-input" : ""}
                />
                {emailErrors.edit && (
                  <div className="error-message">{emailErrors.edit}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="edit-permissions">הרשאות:</label>
                {isEditingSelf ? (
                  <div className="permissions-disabled">
                    <input
                      type="text"
                      value={
                        editForm.permissions === "admin"
                          ? "מנהל"
                          : editForm.permissions === "parentsUser"
                          ? "מחלקת קשר"
                          : editForm.permissions === "childUser"
                          ? "משק קשר"
                          : editForm.permissions
                      }
                      disabled
                      readOnly
                      className="disabled-input"
                    />
                    <small className="permissions-note">
                      ⚠️ לא ניתן לשנות את ההרשאות של המשתמש הנוכחי
                    </small>
                  </div>
                ) : (
                  <select
                    id="edit-permissions"
                    name="permissions"
                    value={editForm.permissions}
                    onChange={handleEditInputChange}
                    required
                  >
                    <option value="admin">מנהל</option>
                    <option value="parentsUser">מחלקת קשר</option>
                    <option value="childUser">משק קשר</option>
                  </select>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="edit-password">סיסמה חדשה (אופציונלי):</label>
                <div className="password-input-wrapper">
                  <input
                    type={showEditPassword ? "text" : "password"}
                    id="edit-password"
                    name="password"
                    value={editForm.password}
                    onChange={handleEditInputChange}
                    placeholder="השאר ריק אם לא רוצה לשנות"
                    className="password-input"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={togglePasswordVisibility}
                    title={showEditPassword ? "הסתר סיסמה" : "הצג סיסמה"}
                  >
                    {showEditPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="save-btn">
                  שמור שינויים
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setEditingUser(null)}
                >
                  ביטול
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="edit-modal">
            <div className="modal-header">
              <h2>הוספת משתמש חדש</h2>
              <button
                className="close-btn"
                onClick={() => setShowAddModal(false)}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleAddUser} className="edit-form">
              <div className="form-group">
                <label htmlFor="add-personnel-search">חיפוש איש צוות:</label>
                <div className="personnel-search-container">
                  <input
                    type="text"
                    id="add-personnel-search"
                    placeholder="הקלד שם או מספר אישי..."
                    value={addPersonnelSearchTerm}
                    onChange={(e) => {
                      setAddPersonnelSearchTerm(e.target.value);
                      setShowAddPersonnelOptions(true);
                    }}
                    onFocus={() => setShowAddPersonnelOptions(true)}
                    onBlur={() =>
                      setTimeout(() => setShowAddPersonnelOptions(false), 200)
                    }
                  />
                  {showAddPersonnelOptions &&
                    filteredAddPersonnel.length > 0 && (
                      <div className="personnel-options">
                        {filteredAddPersonnel.map((person) => (
                          <div
                            key={person.id}
                            className="personnel-option"
                            onClick={() => handleAddPersonnelSelect(person)}
                          >
                            {person.firstName} {person.lastName} (מ.א{" "}
                            {person.personalNumber})
                          </div>
                        ))}
                      </div>
                    )}
                </div>
                <input
                  type="hidden"
                  name="employeeId"
                  value={addForm.employeeId}
                />
              </div>

              <div className="form-group">
                <label htmlFor="add-email">אימייל:</label>
                <input
                  type="email"
                  id="add-email"
                  name="email"
                  value={addForm.email}
                  onChange={handleAddInputChange}
                  required
                  className={emailErrors.add ? "error-input" : ""}
                />
                {emailErrors.add && (
                  <div className="error-message">{emailErrors.add}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="add-permissions">הרשאות:</label>
                <select
                  id="add-permissions"
                  name="permissions"
                  value={addForm.permissions}
                  onChange={handleAddInputChange}
                  required
                >
                  <option value="admin">מנהל</option>
                  <option value="parentsUser">מחלקת קשר</option>
                  <option value="childUser">משק"ש</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="add-password">סיסמה:</label>
                <div className="password-input-wrapper">
                  <input
                    type={showAddPassword ? "text" : "password"}
                    id="add-password"
                    name="password"
                    value={addForm.password}
                    onChange={handleAddInputChange}
                    required
                    className="password-input"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowAddPassword(!showAddPassword)}
                    title={showAddPassword ? "הסתר סיסמה" : "הצג סיסמה"}
                  >
                    {showAddPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="save-btn">
                  הוסף משתמש
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowAddModal(false)}
                >
                  ביטול
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
